import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, first, map, tap } from 'rxjs/operators';
import { AppState } from '../../../app.state';
import { trackEvent } from '@gelato-api-ui/core/analytics/helpers/trackEvent';
import { getFormFieldError } from '../../../lib/getFormFieldError';
import { TitleService } from '../../../shared/services/title.service';
import { ErrorData } from '../../../shared/lib/error-data';
import { CompanyDetails } from '@gelato-api-ui/core/company-details/company-details';
import { CompanyDetailsApiService } from '@gelato-api-ui/core/company-details/services/company-details-api.service';
import { QuickQuestionsService } from '@gelato-api-ui/core/quick-questions/services/quick-questions.service';
import * as companyDetailsActions from '../../../ngrx/company-details.actions';
import { getCompanyDetails, getState } from '../../../ngrx/company-details.reducer';

@Component({
  selector: 'gd-billing-address',
  templateUrl: './billing-address.component.html',
  styleUrls: ['./billing-address.component.scss'],
})
export class BillingAddressComponent implements OnInit {
  originalCompanyDetails$ = this.store.select(getCompanyDetails);
  isLoading$ = this.store.select(getState).pipe(map(state => state.isLoading));
  isSubmittingForm = false;
  error: ErrorData = null;
  companyDetails: CompanyDetails = new CompanyDetails();

  constructor(
    private router: Router,
    private readonly route: ActivatedRoute,
    private store: Store<AppState>,
    private titleService: TitleService,
    private companyDetailsApiService: CompanyDetailsApiService,
    private readonly quickQuestionsService: QuickQuestionsService,
  ) {}

  ngOnInit() {
    this.titleService.setTitle('{txt_meta_title_billing_address}');

    this.originalCompanyDetails$.subscribe((companyDetails: CompanyDetails) => {
      this.companyDetails = {
        ...(companyDetails || new CompanyDetails()),
      };
    });

    this.store.dispatch(new companyDetailsActions.Load(true));
  }

  hasError(fieldName: string): boolean {
    return Boolean(getFormFieldError(this.error, fieldName));
  }

  onCountryIsoCodeChange(countryIsoCode: string) {
    if (countryIsoCode !== this.companyDetails.countryIsoCode) {
      this.companyDetails.stateCode = '';
    }

    this.companyDetails.countryIsoCode = countryIsoCode;
  }

  onPhoneChange(phone: string): void {
    this.companyDetails.phone = phone;
  }

  submit() {
    this.isSubmittingForm = true;

    this.companyDetailsApiService
      .save(this.companyDetails)
      .pipe(
        catchError(error => {
          this.error = error.response.error;

          return of(null);
        }),
      )
      .subscribe(response => {
        if (response) {
          const quickQuestions = this.quickQuestionsService.fromQuestionnaireArray(this.companyDetails.questionnaire);

          trackEvent({
            event: 'logEvent',
            eventType: 'billingAddressSubmitted',
            eventProperties: {
              recipientName: this.companyDetails.recipientName,
              address: this.companyDetails.address,
              postCode: this.companyDetails.postCode,
              city: this.companyDetails.city,
              stateCode: this.companyDetails.stateCode,
              countryIsoCode: this.companyDetails.countryIsoCode,
              phone: this.companyDetails.phone,
              q_ecommerceStoreYearlyRevenue: quickQuestions.ecommerceStoreYearlyRevenue,
            },
          });

          this.store.dispatch(new companyDetailsActions.ResetState());

          this.route.paramMap
            .pipe(
              first(),
              tap(paramMap => {
                const from = paramMap.get('from');
                this.router.navigate([from ? `/${from}` : '/home'], { replaceUrl: true });
              }),
            )
            .subscribe();
        } else {
          this.isSubmittingForm = false;
        }
      });
  }
}
